/**
 * AI Assistant UI Context
 * 
 * Manages the AI assistant's visual state across the app:
 * - Panel open/closed state
 * - Animation state (dashboard card vs sidebar)
 * - Current location awareness
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface AIAssistantUIContextValue {
  /** Whether the assistant panel is open */
  isOpen: boolean;
  /** Toggle the panel */
  togglePanel: () => void;
  /** Open the panel */
  openPanel: () => void;
  /** Close the panel */
  closePanel: () => void;
  /** Whether we're on the dashboard */
  isDashboard: boolean;
  /** Animation state: 'dashboard' | 'sidebar' | 'transitioning' */
  displayMode: 'dashboard' | 'sidebar' | 'transitioning';
}

const AIAssistantUIContext = createContext<AIAssistantUIContextValue | null>(null);

interface AIAssistantUIProviderProps {
  children: ReactNode;
}

export function AIAssistantUIProvider({ children }: AIAssistantUIProviderProps) {
  const location = useLocation();
  const isDashboard = location.pathname === '/';
  const prevPathRef = useRef(location.pathname);
  
  // Default to closed on initial load (don't persist open state)
  const [isOpen, setIsOpen] = useState(false);
  
  const [displayMode, setDisplayMode] = useState<'dashboard' | 'sidebar' | 'transitioning'>(
    isDashboard ? 'dashboard' : 'sidebar'
  );

  // Close panel when navigating to a different page
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      // Route changed - close the panel
      setIsOpen(false);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Handle display mode transitions
  useEffect(() => {
    const newMode = isDashboard ? 'dashboard' : 'sidebar';
    
    if (displayMode !== newMode && displayMode !== 'transitioning') {
      setDisplayMode('transitioning');
      // After animation completes, set final state
      const timer = setTimeout(() => {
        setDisplayMode(newMode);
      }, 400); // Match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isDashboard, displayMode]);

  // NOTE: We don't persist panel open state - always starts collapsed

  // Keyboard shortcut: Cmd/Ctrl + K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const togglePanel = useCallback(() => setIsOpen(prev => !prev), []);
  const openPanel = useCallback(() => setIsOpen(true), []);
  const closePanel = useCallback(() => setIsOpen(false), []);

  return (
    <AIAssistantUIContext.Provider
      value={{
        isOpen,
        togglePanel,
        openPanel,
        closePanel,
        isDashboard,
        displayMode,
      }}
    >
      {children}
    </AIAssistantUIContext.Provider>
  );
}

export function useAIAssistantUI(): AIAssistantUIContextValue {
  const context = useContext(AIAssistantUIContext);
  if (!context) {
    throw new Error('useAIAssistantUI must be used within AIAssistantUIProvider');
  }
  return context;
}
